import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@/../environments/environment';

export interface ResumoEstatisticas {
  pendentes: number;
  concluidas: number;
  total: number;
}

export interface DashboardGeralResponse {
  resumo: {
    isencoes: ResumoEstatisticas;
    cotas: ResumoEstatisticas;
    condicoesEspeciais: ResumoEstatisticas;
    recursos: ResumoEstatisticas;
    avaliacoes: ResumoEstatisticas;
    titulos: ResumoEstatisticas;
  };
}

// Interfaces para Editais Designados
export interface EtapaAtual {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CargoEdital {
  id: number;
  nome: string;
  nivel: string;
  vagas: number;
  cadastroReserva: number;
  apenasCadastroReserva: boolean;
  taxaInscricao: number;
}

export interface EditalDesignado {
  id: number;
  numeroEdital: string;
  orgaoId: number;
  orgaoNome: string;
  nome: string;
  descricao: string;
  fotoUrl: string;
  versao: number;
  dtPublicacao: string;
  status: string;
  criadoEm: string;
  atualizadoEm: string;
  tag: string;
  etapaAtual: EtapaAtual;
  etapaAtualDescricao: string;
  totalCargos: number;
  totalEtapas: number;
  totalDocumentos: number;
  quantidadeInscritos: number;
  quantidadeConfirmados: number;
  quantidadePendentes: number;
  arrecadado: number;
  vagasFormatadas: string;
  possuiCotas: boolean;
  possuiCondicoesEspeciais: boolean;
  inscricoesGratuitas: boolean;
  podeEditar: boolean;
  requerRetificacao: boolean;
  cargos: CargoEdital[];
  etapas: any[];
  documentos: any[];
  cotasDisponiveis: any[];
  condicoesDisponiveis: any[];
}

// Interfaces para Títulos
export interface InscricaoTitulo {
  id: number;
  inscricaoId: number;
  editalTituloId: number;
  tituloNome: string;
  tituloDescricao: string;
  valorUnitario: number;
  valorMaximo: number;
  documentoUrl: string;
  nomeArquivo: string;
  tamanhoBytes: number;
  status: 'PENDENTE' | 'DEFERIDO' | 'INDEFERIDO';
  createdAt: string;
  updatedAt: string;
}

export interface AvaliacaoTitulo {
  id: number;
  inscricaoTituloId: number;
  avaliadorId: number;
  avaliadorNome: string;
  avaliadorEmail: string;
  pontosAtribuidos: number;
  decisao: 'DEFERIDO' | 'INDEFERIDO';
  justificativa: string;
  createdAt: string;
  updatedAt: string;
}

export interface CandidatoComTitulos {
  candidatoId: number;
  candidatoNome: string;
  candidatoCpf: string;
  inscricaoId: number;
  titulos: InscricaoTitulo[];
}

export interface AvaliarTituloRequest {
  pontosAtribuidos: number;
  decisao: 'DEFERIDO' | 'INDEFERIDO';
  justificativa: string;
}

// Interfaces para Dashboard
export interface AvaliadorDashboardDTO {
  editalId: number;
  editalNome: string;
  resumo: {
    isencoes: {
      pendentes: number;
      deferidas: number;
      indeferidas: number;
    };
    cotas: {
      pendentes: number;
      deferidas: number;
      indeferidas: number;
    };
    condicoesEspeciais: {
      pendentes: number;
      deferidas: number;
      indeferidas: number;
    };
    recursos: {
      pendentes: number;
      deferidos: number;
      indeferidos: number;
    };
  };
}

// Interfaces para Candidato
export interface CandidatoBasicoDTO {
  id: number;
  nome: string;
  cpf: string;
  email: string;
}

// Interfaces para Candidato Completo (da API)
export interface CandidatoCompletoDTO {
  id: number;
  cpf: string;
  nomeCompleto: string;
  sexo?: string;
  desejaNomeSocial?: boolean;
  nomeSocial?: string;
  dataNascimento?: string;
  email?: string;
  telefonePessoal?: string;
  [key: string]: any; // Para outros campos que possam existir
}

// Interfaces para Inscricao (da API)
export interface InscricaoDTO {
  id: number;
  candidato: CandidatoCompletoDTO;
  cargo?: any;
  valorPago?: number;
  pago?: boolean;
  [key: string]: any;
}

// Interfaces para EtapaIsencao (da API)
export interface EtapaIsencaoDTO {
  id: number;
  etapa?: any;
  tipoIsencao: TipoIsencaoDTO;
  porcentagemDesconto: number;
  documentos?: any[];
  [key: string]: any;
}

// Interfaces para Isenções
export interface TipoIsencaoDTO {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  porcentagemDesconto?: number;
  ativo: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface InscricaoIsencaoResponseDTO {
  id: number;
  inscricaoId?: number;
  // Estrutura real da API
  inscricao?: InscricaoDTO;
  etapaIsencao?: EtapaIsencaoDTO;
  status: 'PENDENTE' | 'DEFERIDO' | 'INDEFERIDO';
  documentoUrl?: string;
  porcentagemAplicada?: number;
  valorDesconto?: number;
  createdAt: string;
  updatedAt: string;
  // Campos mapeados para facilitar o uso no componente (preenchidos no mapeamento)
  candidato?: CandidatoBasicoDTO;
  tipoIsencao?: TipoIsencaoDTO;
}

export interface InscricaoIsencaoDetalhadaResponseDTO extends InscricaoIsencaoResponseDTO {
  historicoAvaliacoes?: AvaliacaoIsencaoDTO[];
  documentosObrigatorios?: DocumentoDTO[];
  documentosOpcionais?: DocumentoDTO[];
  statusDescricao?: string;
  porcentagemDesconto?: number;
}

// Interfaces para Cotas
export interface TipoCotaDTO {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  ativo: boolean;
}

export interface DocumentoCotaDTO {
  id: number;
  tipoDocumento: {
    id: number;
    codigo: string;
    nome: string;
    descricao?: string;
    ativo?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
  documentoUrl: string | null;
  nomeArquivo: string | null;
  tamanhoBytes?: number;
  obrigatorio: boolean;
}

// Interface genérica para documentos (usada em isenções, recursos, condições especiais)
export interface DocumentoDTO {
  id: number;
  tipoDocumento: {
    id: number;
    codigo: string;
    nome: string;
    descricao?: string;
    ativo?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
  documentoUrl: string | null;
  nomeArquivo: string | null;
  obrigatorio: boolean;
}

export interface InscricaoCotaResponseDTO {
  id: number;
  inscricaoId: number;
  candidato: CandidatoBasicoDTO;
  tipoCota: TipoCotaDTO;
  status: 'PENDENTE' | 'DEFERIDO' | 'INDEFERIDO';
  documentos?: DocumentoCotaDTO[];
  createdAt: string;
  updatedAt: string;
}

export interface InscricaoCotaDetalhadaResponseDTO extends InscricaoCotaResponseDTO {
  historicoAvaliacoes?: AvaliacaoCotaCondicaoDTO[];
  documentosObrigatorios?: DocumentoCotaDTO[];
  documentosOpcionais?: DocumentoCotaDTO[];
  statusDescricao?: string;
  percentualVagas?: string;
  vagasReservadas?: number;
  ultimaAvaliacao?: AvaliacaoCotaCondicaoDTO;
}

// Interfaces para Condições Especiais
export interface TipoCondicaoEspecialDTO {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  ativo: boolean;
}

export interface DocumentoCondicaoEspecialDTO {
  id: number;
  tipoDocumento: {
    id: number;
    codigo: string;
    nome: string;
    descricao?: string;
    ativo?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
  documentoUrl: string | null;
  nomeArquivo: string | null;
  tamanhoBytes?: number;
  obrigatorio: boolean;
}

export interface InscricaoCondicaoEspecialResponseDTO {
  id: number;
  inscricaoId: number;
  candidato: CandidatoBasicoDTO;
  tipoCondicaoEspecial: TipoCondicaoEspecialDTO;
  status: 'PENDENTE' | 'DEFERIDO' | 'INDEFERIDO';
  documentos?: DocumentoCondicaoEspecialDTO[];
  createdAt: string;
  updatedAt: string;
}

export interface InscricaoCondicaoEspecialDetalhadaResponseDTO extends InscricaoCondicaoEspecialResponseDTO {
  historicoAvaliacoes?: AvaliacaoCotaCondicaoDTO[];
  documentosObrigatorios?: DocumentoCondicaoEspecialDTO[];
  documentosOpcionais?: DocumentoCondicaoEspecialDTO[];
  statusDescricao?: string;
  descricaoSolicitacao?: string;
  ultimaAvaliacao?: AvaliacaoCotaCondicaoDTO;
}

// Interfaces para Recursos
export interface TipoRecursoDTO {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  ativo: boolean;
}

export interface EtapaRecursoDTO {
  id: number;
  dtInicio: string;
  dtFim: string;
}

export interface InscricaoRecursoResponseDTO {
  id: number;
  inscricaoId: number;
  candidato: CandidatoBasicoDTO;
  tipoRecurso: TipoRecursoDTO;
  etapaRecurso: EtapaRecursoDTO;
  status: 'PENDENTE' | 'DEFERIDO' | 'INDEFERIDO';
  textoRecurso: string;
  documentoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InscricaoRecursoDetalhadaResponseDTO extends InscricaoRecursoResponseDTO {
  historicoAvaliacoes?: any[];
  documentosObrigatorios?: DocumentoDTO[];
  documentosOpcionais?: DocumentoDTO[];
  statusDescricao?: string;
}

// Interfaces para Avaliações
export interface AvaliacaoRequestDTO {
  decisao: 'DEFERIDO' | 'INDEFERIDO';
  justificativa: string;
}

export interface AvaliacaoIsencaoDTO {
  id: number;
  avaliadorId: number;
  avaliadorNome: string;
  etapa: 'ANALISE_INICIAL' | 'RECURSO';
  decisao: 'DEFERIDO' | 'INDEFERIDO';
  justificativa: string;
  recursoTexto?: string;
  createdAt: string;
}

export interface AvaliacaoCotaCondicaoDTO {
  id: number;
  inscricaoCotaId?: number;
  inscricaoCondicaoEspecialId?: number;
  tipo: 'COTA' | 'CONDICAO_ESPECIAL';
  etapa: 'ANALISE_INICIAL' | 'RECURSO';
  avaliadorId: number;
  avaliadorNome: string;
  avaliadorEmail: string;
  decisao: 'DEFERIDO' | 'INDEFERIDO';
  justificativa: string;
  recursoTexto?: string;
  createdAt: string;
  updatedAt: string;
}

// Interfaces para Pendentes
export interface PendentesResponse {
  cotasPendentes: InscricaoCotaResponseDTO[];
  condicoesPendentes: InscricaoCondicaoEspecialResponseDTO[];
}

@Injectable({
  providedIn: 'root',
})
export class AvaliadorService {
  private apiUrl = `${environment.apiUrl}/avaliador`;
  private avaliadoresUrl = `${environment.apiUrl}/avaliadores`;

  constructor(private http: HttpClient) {}

  /**
   * Dashboard geral do avaliador
   * GET /api/avaliador/avaliacoes/dashboard-geral
   */
  getDashboardGeral(): Observable<DashboardGeralResponse> {
    return this.http.get<DashboardGeralResponse>(`${this.apiUrl}/avaliacoes/dashboard-geral`);
  }

  /**
   * Lista editais designados ao avaliador
   * GET /api/avaliadores/meus-editais
   */
  getMeusEditais(): Observable<EditalDesignado[]> {
    return this.http.get<EditalDesignado[]>(`${this.avaliadoresUrl}/meus-editais`);
  }

  /**
   * Busca detalhes de um edital designado
   * GET /api/avaliadores/meus-editais/{editalId}
   */
  getEditalDesignado(editalId: number): Observable<EditalDesignado> {
    return this.http.get<EditalDesignado>(`${this.avaliadoresUrl}/meus-editais/${editalId}`);
  }

  /**
   * Visualizar detalhes de um título
   * GET /api/avaliador/titulos/{inscricaoTituloId}
   */
  getTituloDetalhes(inscricaoTituloId: number): Observable<InscricaoTitulo> {
    return this.http.get<InscricaoTitulo>(`${this.apiUrl}/titulos/${inscricaoTituloId}`);
  }

  /**
   * Listar avaliações de um título
   * GET /api/avaliador/titulos/{inscricaoTituloId}/avaliacoes
   */
  getAvaliacoesTitulo(inscricaoTituloId: number): Observable<AvaliacaoTitulo[]> {
    return this.http.get<AvaliacaoTitulo[]>(`${this.apiUrl}/titulos/${inscricaoTituloId}/avaliacoes`);
  }

  /**
   * Listar títulos de uma inscrição
   * GET /api/avaliador/titulos/inscricao/{inscricaoId}
   */
  getTitulosInscricao(inscricaoId: number): Observable<InscricaoTitulo[]> {
    return this.http.get<InscricaoTitulo[]>(`${this.apiUrl}/titulos/inscricao/${inscricaoId}`);
  }

  /**
   * Listar candidatos com títulos para avaliar
   * GET /api/avaliador/titulos/edital/{editalId}/candidatos
   */
  getCandidatosComTitulos(editalId: number): Observable<CandidatoComTitulos[]> {
    return this.http.get<CandidatoComTitulos[]>(`${this.apiUrl}/titulos/edital/${editalId}/candidatos`);
  }

  /**
   * Listar minhas avaliações
   * GET /api/avaliador/titulos/avaliador/minhas-avaliacoes
   */
  getMinhasAvaliacoes(): Observable<AvaliacaoTitulo[]> {
    return this.http.get<AvaliacaoTitulo[]>(`${this.apiUrl}/titulos/avaliador/minhas-avaliacoes`);
  }

  /**
   * Avaliar título
   * POST /api/avaliador/titulos/{inscricaoTituloId}/avaliar
   */
  avaliarTitulo(inscricaoTituloId: number, request: AvaliarTituloRequest): Observable<AvaliacaoTitulo> {
    return this.http.post<AvaliacaoTitulo>(`${this.apiUrl}/titulos/${inscricaoTituloId}/avaliar`, request);
  }

  // ==================== DASHBOARD ====================

  /**
   * Dashboard do avaliador por edital
   * GET /api/avaliador/dashboard?editalId={editalId}
   */
  getDashboard(editalId: number): Observable<AvaliadorDashboardDTO> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<AvaliadorDashboardDTO>(`${this.apiUrl}/dashboard`, { params });
  }

  // ==================== PENDENTES ====================

  /**
   * Listar todas as solicitações pendentes (Cotas + Condições Especiais)
   * GET /api/avaliador/avaliacoes/pendentes?editalId={editalId}
   */
  getPendentes(editalId: number): Observable<PendentesResponse> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<PendentesResponse>(`${this.apiUrl}/avaliacoes/pendentes`, { params });
  }

  /**
   * Listar todas as isenções
   * GET /api/avaliador/avaliacoes/isencoes?editalId={editalId}
   * 
   * Lista todas as solicitações de isenção (pendentes, deferidas ou indeferidas) de um edital.
   * Se editalId não for fornecido, retorna isenções de todos os editais que o avaliador tem permissão ISENCAO_VISUALIZAR.
   * 
   * A resposta inclui:
   * - Informações básicas do candidato (candidatoNome, candidatoCpf)
   * - Tipo de isenção (tipoIsencaoNome, tipoIsencaoCodigo, porcentagemDesconto)
   * - Status da isenção (PENDENTE, DEFERIDO, INDEFERIDO)
   * - Documento anexado (documentoUrl)
   * - Informações de desconto aplicado (porcentagemAplicada, valorDesconto) se deferida
   */
  getIsencoes(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/isencoes`, { params });
  }

  /**
   * Listar isenções pendentes
   * GET /api/avaliador/avaliacoes/isencoes/pendentes?editalId={editalId}
   * 
   * Lista todas as solicitações de isenção pendentes de um edital.
   * Se editalId não for fornecido, retorna isenções de todos os editais que o avaliador tem permissão ISENCAO_VISUALIZAR.
   * 
   * @deprecated Use getIsencoes() para listar todas as isenções (pendentes, deferidas e indeferidas)
   */
  getIsencoesPendentes(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/isencoes/pendentes`, { params });
  }

  /**
   * Listar todas as cotas
   * GET /api/avaliador/avaliacoes/cotas?editalId={editalId}
   * 
   * Lista todas as solicitações de cotas (pendentes, deferidas ou indeferidas) de um edital.
   * Se editalId não for fornecido, retorna cotas de todos os editais que o avaliador tem permissão COTA_VISUALIZAR.
   * 
   * A resposta inclui:
   * - Informações básicas do candidato (candidatoNome, candidatoCpf)
   * - Tipo de cota (editalCota.tipoCota)
   * - Status da cota (PENDENTE, DEFERIDO, INDEFERIDO)
   * - Documento anexado (documentoUrl)
   */
  getCotas(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/cotas`, { params });
  }

  /**
   * Listar cotas pendentes
   * GET /api/avaliador/avaliacoes/cotas/pendentes?editalId={editalId}
   * 
   * Lista todas as solicitações de cotas pendentes de um edital.
   * Se editalId não for fornecido, retorna cotas de todos os editais que o avaliador tem permissão COTA_VISUALIZAR.
   * 
   * @deprecated Use getCotas() para listar todas as cotas (pendentes, deferidas e indeferidas)
   */
  getCotasPendentes(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/cotas/pendentes`, { params });
  }

  /**
   * Listar todas as condições especiais
   * GET /api/avaliador/avaliacoes/condicoes-especiais?editalId={editalId}
   * 
   * Lista todas as solicitações de condições especiais (pendentes, deferidas ou indeferidas) de um edital.
   * Se editalId não for fornecido, retorna condições especiais de todos os editais que o avaliador tem permissão CONDICAO_ESPECIAL_VISUALIZAR.
   * 
   * A resposta inclui:
   * - Informações básicas do candidato (candidatoNome, candidatoCpf)
   * - Tipo de condição especial (editalCondicaoEspecial.tipoCondicaoEspecial)
   * - Status da condição especial (PENDENTE, DEFERIDO, INDEFERIDO)
   * - Descrição da solicitação (descricaoSolicitacao)
   * - Documento anexado (documentoUrl)
   */
  getCondicoesEspeciais(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/condicoes-especiais`, { params });
  }

  /**
   * Listar condições especiais pendentes
   * GET /api/avaliador/avaliacoes/condicoes-especiais/pendentes?editalId={editalId}
   * 
   * Lista todas as solicitações de condições especiais pendentes de um edital.
   * Se editalId não for fornecido, retorna condições especiais de todos os editais que o avaliador tem permissão CONDICAO_ESPECIAL_VISUALIZAR.
   * 
   * @deprecated Use getCondicoesEspeciais() para listar todas as condições especiais (pendentes, deferidas e indeferidas)
   */
  getCondicoesEspeciaisPendentes(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/condicoes-especiais/pendentes`, { params });
  }

  /**
   * Listar todos os recursos
   * GET /api/avaliador/avaliacoes/recursos?editalId={editalId}
   * 
   * Lista todas as solicitações de recursos (pendentes, deferidos ou indeferidos) de um edital.
   * Se editalId não for fornecido, retorna recursos de todos os editais que o avaliador tem permissão RECURSO_VISUALIZAR.
   * 
   * A resposta inclui:
   * - Informações básicas do candidato (candidatoNome, candidatoCpf)
   * - Tipo de recurso (tipoRecursoNome, tipoRecursoCodigo)
   * - Status do recurso (PENDENTE, DEFERIDO, INDEFERIDO)
   * - Texto do recurso (textoRecurso)
   * - Documento anexado (documentoUrl)
   * - Etapa do recurso (etapaRecurso com dtInicio e dtFim)
   */
  getRecursos(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/recursos`, { params });
  }

  /**
   * Listar recursos pendentes
   * GET /api/avaliador/avaliacoes/recursos/pendentes?editalId={editalId}
   * 
   * Lista todas as solicitações de recursos pendentes de um edital.
   * Se editalId não for fornecido, retorna recursos de todos os editais que o avaliador tem permissão RECURSO_VISUALIZAR.
   * 
   * @deprecated Use getRecursos() para listar todos os recursos (pendentes, deferidos e indeferidos)
   */
  getRecursosPendentes(editalId?: number): Observable<any[]> {
    let params = new HttpParams();
    if (editalId) {
      params = params.set('editalId', editalId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/recursos/pendentes`, { params });
  }

  /**
   * Listar solicitações por status
   * GET /api/avaliador/avaliacoes/{tipo}/status/{status}?editalId={editalId}
   */
  getSolicitacoesPorStatus(
    tipo: 'isencao' | 'cota' | 'condicao-especial' | 'recurso',
    status: 'PENDENTE' | 'DEFERIDO' | 'INDEFERIDO',
    editalId: number
  ): Observable<any[]> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/${tipo}/status/${status}`, { params });
  }

  // ==================== ISENÇÕES ====================

  /**
   * Visualizar detalhes de uma isenção
   * GET /api/avaliador/avaliacoes/isencoes/{inscricaoIsencaoId}
   */
  getIsencaoDetalhes(inscricaoIsencaoId: number): Observable<InscricaoIsencaoDetalhadaResponseDTO> {
    return this.http.get<InscricaoIsencaoDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/isencoes/${inscricaoIsencaoId}`);
  }

  /**
   * Avaliar isenção
   * POST /api/avaliador/avaliacoes/isencao/{inscricaoIsencaoId}/avaliar
   */
  avaliarIsencao(inscricaoIsencaoId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/isencao/${inscricaoIsencaoId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de avaliações de isenção
   * GET /api/avaliador/avaliacoes/isencoes/{inscricaoIsencaoId}/historico
   * 
   * Retorna o histórico completo de avaliações de uma isenção, incluindo:
   * - Avaliações iniciais
   * - Recursos e suas avaliações
   * - Informações do avaliador (nome, ID)
   * - Decisão e justificativa de cada avaliação
   * - Data de criação
   */
  getIsencaoHistorico(inscricaoIsencaoId: number): Observable<AvaliacaoIsencaoDTO[]> {
    return this.http.get<AvaliacaoIsencaoDTO[]>(`${this.apiUrl}/avaliacoes/isencoes/${inscricaoIsencaoId}/historico`);
  }

  // ==================== COTAS ====================

  /**
   * Visualizar detalhes de uma cota
   * GET /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}
   */
  getCotaDetalhes(inscricaoCotaId: number): Observable<InscricaoCotaDetalhadaResponseDTO> {
    return this.http.get<InscricaoCotaDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}`);
  }

  /**
   * Avaliar cota
   * POST /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}/avaliar
   */
  avaliarCota(inscricaoCotaId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de avaliações de cota
   * GET /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}/historico
   */
  getCotaHistorico(inscricaoCotaId: number): Observable<AvaliacaoCotaCondicaoDTO[]> {
    return this.http.get<AvaliacaoCotaCondicaoDTO[]>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}/historico`);
  }

  /**
   * Avaliar recurso de cota
   * POST /api/avaliador/avaliacoes/cotas/{inscricaoCotaId}/recurso/avaliar
   */
  avaliarRecursoCota(inscricaoCotaId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/cotas/${inscricaoCotaId}/recurso/avaliar`, request);
  }

  // ==================== CONDIÇÕES ESPECIAIS ====================

  /**
   * Visualizar detalhes de uma condição especial
   * GET /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}
   */
  getCondicaoEspecialDetalhes(inscricaoCondicaoEspecialId: number): Observable<InscricaoCondicaoEspecialDetalhadaResponseDTO> {
    return this.http.get<InscricaoCondicaoEspecialDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}`);
  }

  /**
   * Avaliar condição especial
   * POST /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}/avaliar
   */
  avaliarCondicaoEspecial(inscricaoCondicaoEspecialId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de avaliações de condição especial
   * GET /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}/historico
   */
  getCondicaoEspecialHistorico(inscricaoCondicaoEspecialId: number): Observable<AvaliacaoCotaCondicaoDTO[]> {
    return this.http.get<AvaliacaoCotaCondicaoDTO[]>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}/historico`);
  }

  /**
   * Avaliar recurso de condição especial
   * POST /api/avaliador/avaliacoes/condicoes-especiais/{inscricaoCondicaoEspecialId}/recurso/avaliar
   */
  avaliarRecursoCondicaoEspecial(inscricaoCondicaoEspecialId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/condicoes-especiais/${inscricaoCondicaoEspecialId}/recurso/avaliar`, request);
  }

  // ==================== RECURSOS ====================

  /**
   * Visualizar detalhes de um recurso
   * GET /api/avaliador/avaliacoes/recursos/{inscricaoRecursoId}
   */
  getRecursoDetalhes(inscricaoRecursoId: number): Observable<InscricaoRecursoDetalhadaResponseDTO> {
    return this.http.get<InscricaoRecursoDetalhadaResponseDTO>(`${this.apiUrl}/avaliacoes/recursos/${inscricaoRecursoId}`);
  }

  /**
   * Avaliar recurso
   * POST /api/avaliador/avaliacoes/recurso/{inscricaoRecursoId}/avaliar
   */
  avaliarRecurso(inscricaoRecursoId: number, request: AvaliacaoRequestDTO): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/avaliacoes/recurso/${inscricaoRecursoId}/avaliar`, request);
  }

  /**
   * Visualizar histórico de recursos
   * GET /api/avaliador/avaliacoes/recursos/{inscricaoRecursoId}/historico
   */
  getRecursoHistorico(inscricaoRecursoId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/avaliacoes/recursos/${inscricaoRecursoId}/historico`);
  }

  // ==================== UTILITÁRIOS ====================

  /**
   * Buscar candidato por ID
   * GET /api/avaliador/candidatos/{candidatoId}?editalId={editalId}
   */
  getCandidatoPorId(candidatoId: number, editalId: number): Observable<CandidatoBasicoDTO> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<CandidatoBasicoDTO>(`${this.apiUrl}/candidatos/${candidatoId}`, { params });
  }

  /**
   * Buscar candidato por CPF
   * GET /api/avaliador/candidatos/cpf/{cpf}?editalId={editalId}
   */
  getCandidatoPorCpf(cpf: string, editalId: number): Observable<CandidatoBasicoDTO> {
    const params = new HttpParams().set('editalId', editalId.toString());
    return this.http.get<CandidatoBasicoDTO>(`${this.apiUrl}/candidatos/cpf/${cpf}`, { params });
  }

  /**
   * Visualizar documento anexado
   * GET /api/avaliador/documentos/{documentoId}/download
   */
  downloadDocumento(documentoId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/documentos/${documentoId}/download`, { responseType: 'blob' });
  }

  /**
   * Listar tipos de recurso disponíveis no edital
   * GET /api/avaliador/editais/{editalId}/tipos-recursos
   */
  getTiposRecursos(editalId: number): Observable<TipoRecursoDTO[]> {
    return this.http.get<TipoRecursoDTO[]>(`${this.apiUrl}/editais/${editalId}/tipos-recursos`);
  }

  /**
   * Listar tipos de isenção disponíveis no edital
   * GET /api/avaliador/editais/{editalId}/tipos-isencao
   */
  getTiposIsencao(editalId: number): Observable<TipoIsencaoDTO[]> {
    return this.http.get<TipoIsencaoDTO[]>(`${this.apiUrl}/editais/${editalId}/tipos-isencao`);
  }

  /**
   * Listar tipos de cota disponíveis no edital
   * GET /api/avaliador/editais/{editalId}/tipos-cotas
   */
  getTiposCotas(editalId: number): Observable<TipoCotaDTO[]> {
    return this.http.get<TipoCotaDTO[]>(`${this.apiUrl}/editais/${editalId}/tipos-cotas`);
  }

  /**
   * Listar tipos de condição especial disponíveis no edital
   * GET /api/avaliador/editais/{editalId}/tipos-condicoes-especiais
   */
  getTiposCondicoesEspeciais(editalId: number): Observable<TipoCondicaoEspecialDTO[]> {
    return this.http.get<TipoCondicaoEspecialDTO[]>(`${this.apiUrl}/editais/${editalId}/tipos-condicoes-especiais`);
  }
}
